import classNames from 'classnames';
import Markdown from 'markdown-to-jsx';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { visibleGuides, visiblePages } from './audiences';
import { DOC_PAGES } from './content';
import { UI, useDocsLocale } from './locale';
import { buildIndex, search, SearchResult, slugify } from './search';
import { DocPage, Guide, Locale, LOCALES } from './types';

/**
 * The documentation, at `/docs/<page>`: the user guide for every role, plus the data managers' guide for those who
 * hold a backoffice permission. Each page is shown to the users it is written for (audiences.ts), in English or
 * French, with a search over the pages the user may read. Inside GdataLayout, so an account without a role is
 * sent to /forbidden before it gets here.
 */
export function DocsPage({ pages = DOC_PAGES }: { pages?: DocPage[] }) {
  const { has, isLoading } = usePermissions();
  const { pageId } = useParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [locale, setLocale] = useDocsLocale();
  const [query, setQuery] = useState('');
  const ui = UI[locale];

  const readable = useMemo(() => visiblePages(pages, has), [pages, has]);
  const guides = visibleGuides(readable);
  const index = useMemo(() => buildIndex(readable, locale), [readable, locale]);
  const results = useMemo(() => search(index, query), [index, query]);

  const page = readable.find(p => p.id === pageId);

  useEffect(() => {
    if (!page) {
      return;
    }
    const target = hash
      ? document.getElementById(decodeURIComponent(hash.slice(1)))
      : null;
    if (target) {
      target.scrollIntoView?.({ block: 'start' });
    } else {
      window.scrollTo?.({ top: 0 });
    }
  }, [page, hash, locale]);

  if (isLoading) {
    return null;
  }
  if (!pageId && readable.length > 0) {
    // the data managers start on their own guide
    const first =
      readable.find(p => p.guide === guides[guides.length - 1]) ?? readable[0];
    return <Navigate to={`/docs/${first.id}`} replace />;
  }

  const guide: Guide = page?.guide ?? guides[0] ?? 'user';
  const inGuide = readable.filter(p => p.guide === guide);
  const position = page ? inGuide.indexOf(page) : -1;

  const open = (result: SearchResult) => {
    setQuery('');
    const { pageId: id, anchor } = result.section;
    navigate(`/docs/${id}${anchor ? `#${anchor}` : ''}`);
  };

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6" lang={locale}>
      <div className="flex flex-wrap items-center gap-4">
        <h1 className="mr-auto text-3xl font-semibold tracking-tight text-dl-text">
          {ui.title}
        </h1>
        {guides.length > 1 && (
          <Segmented
            label={ui.guides}
            options={guides.map(g => ({
              value: g,
              label: g === 'admin' ? ui.adminGuide : ui.userGuide,
            }))}
            value={guide}
            onChange={g => {
              const first = readable.find(p => p.guide === g);
              setQuery('');
              first && navigate(`/docs/${first.id}`);
            }}
          />
        )}
        <Segmented
          label={ui.language}
          options={LOCALES.map(l => ({ value: l, label: l.toUpperCase() }))}
          value={locale}
          onChange={l => setLocale(l as Locale)}
        />
      </div>

      <div className="relative">
        <label className="sr-only" htmlFor="docs-search">
          {ui.searchLabel}
        </label>
        <input
          id="docs-search"
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && results[0]) {
              open(results[0]);
            } else if (event.key === 'Escape') {
              setQuery('');
            }
          }}
          placeholder={ui.searchPlaceholder}
          className="h-12 w-full rounded-xl border-0 bg-dl-surface px-4 text-lg text-dl-text ring-1 ring-dl-border placeholder:text-dl-faint focus:outline-none focus:ring-2 focus:ring-dl-primary"
        />
      </div>

      <div className="flex gap-8">
        <nav
          aria-label={guide === 'admin' ? ui.adminGuide : ui.userGuide}
          className="sticky top-24 hidden max-h-[calc(100vh-8rem)] w-64 shrink-0 self-start overflow-y-auto lg:block"
        >
          {groupBySection(inGuide, locale).map(([section, sectionPages]) => (
            <div key={section} className="mb-5">
              <div className="mb-1 text-sm font-semibold uppercase tracking-wide text-dl-faint">
                {section}
              </div>
              <ul>
                {sectionPages.map(p => (
                  <li key={p.id}>
                    <Link
                      to={`/docs/${p.id}`}
                      onClick={() => setQuery('')}
                      aria-current={p === page && !query ? 'page' : undefined}
                      className={classNames(
                        'block rounded-lg px-3 py-1.5 text-base transition',
                        {
                          'bg-dl-tint font-semibold text-dl-primary':
                            p === page && !query,
                          'text-dl-muted hover:bg-dl-sunken hover:text-dl-text':
                            p !== page || query,
                        }
                      )}
                    >
                      {p.text[locale].title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* contain: the worklist surface is as wide as its content's min-content, and a code block's longest
            line would otherwise widen the page past the screen (and the header's account button with it) */}
        <main className="min-w-0 flex-1 [contain:inline-size]">
          {query.trim() ? (
            <Results
              query={query}
              results={results}
              locale={locale}
              onOpen={open}
            />
          ) : page ? (
            <Article
              page={page}
              locale={locale}
              previous={inGuide[position - 1]}
              next={inGuide[position + 1]}
            />
          ) : (
            <p className="text-lg text-dl-muted">{ui.notFound}</p>
          )}
        </main>
      </div>
    </div>
  );
}

function groupBySection(
  pages: DocPage[],
  locale: Locale
): [string, DocPage[]][] {
  const groups = new Map<string, DocPage[]>();
  for (const page of pages) {
    const section = page.section[locale];
    groups.set(section, [...(groups.get(section) ?? []), page]);
  }
  return [...groups];
}

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex rounded-full bg-dl-surface p-1 shadow-sm ring-1 ring-dl-border"
    >
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={classNames('rounded-full px-4 py-1 text-base transition', {
            'bg-dl-primary text-white': option.value === value,
            'text-dl-muted hover:bg-dl-tint': option.value !== value,
          })}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Results({
  query,
  results,
  locale,
  onOpen,
}: {
  query: string;
  results: SearchResult[];
  locale: Locale;
  onOpen: (result: SearchResult) => void;
}) {
  const ui = UI[locale];
  if (results.length === 0) {
    return (
      <p className="text-lg text-dl-muted">{ui.noResults(query.trim())}</p>
    );
  }
  return (
    <section aria-label={ui.results(results.length)}>
      <p className="mb-3 text-base text-dl-faint">
        {ui.results(results.length)}
      </p>
      <ul className="flex flex-col gap-2">
        {results.map(result => (
          <li key={`${result.section.pageId}#${result.section.anchor ?? ''}`}>
            <button
              type="button"
              onClick={() => onOpen(result)}
              className="w-full rounded-xl border border-dl-border bg-dl-surface p-4 text-left transition hover:border-dl-strong hover:bg-dl-raised"
            >
              <div className="text-lg font-semibold text-dl-text">
                {result.section.pageTitle}
                {result.section.heading && (
                  <span className="font-normal text-dl-muted">
                    {' '}
                    › {result.section.heading}
                  </span>
                )}
              </div>
              <p className="mt-1 text-base text-dl-muted">
                <Highlighted text={result.snippet} ranges={result.highlights} />
              </p>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Highlighted({
  text,
  ranges,
}: {
  text: string;
  ranges: [number, number][];
}) {
  const parts: React.ReactNode[] = [];
  let at = 0;
  ranges.forEach(([start, end], i) => {
    parts.push(text.slice(at, start));
    parts.push(
      <mark key={i} className="rounded bg-dl-warning-tint px-0.5 text-dl-text">
        {text.slice(start, end)}
      </mark>
    );
    at = end;
  });
  parts.push(text.slice(at));
  return <>{parts}</>;
}

/** The page's sections, from its `##` headings. */
function headingsOf(body: string): string[] {
  return body
    .split('\n')
    .map(line => line.match(/^##\s+(.+?)\s*$/)?.[1])
    .filter(Boolean);
}

function Article({
  page,
  locale,
  previous,
  next,
}: {
  page: DocPage;
  locale: Locale;
  previous?: DocPage;
  next?: DocPage;
}) {
  const ui = UI[locale];
  const { title, summary, body } = page.text[locale];
  const headings = headingsOf(body);
  return (
    <div className="flex gap-8">
      <article className="min-w-0 flex-1 rounded-2xl border border-dl-border bg-dl-surface px-8 py-7">
        <div className="mb-1 text-sm font-semibold uppercase tracking-wide text-dl-faint">
          {page.section[locale]}
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-dl-text">
          {title}
        </h2>
        <p className="mt-2 text-lg text-dl-muted">{summary}</p>
        <Markdown
          className="docs-prose mt-6"
          options={{
            slugify,
            overrides: {
              a: { component: DocLink },
              img: { component: Screenshot },
            },
          }}
        >
          {body}
        </Markdown>
        <div className="mt-10 flex justify-between gap-4 border-t border-dl-border pt-5 text-base">
          {previous ? (
            <Link
              to={`/docs/${previous.id}`}
              className="text-dl-primary hover:underline"
            >
              ← {ui.previous}: {previous.text[locale].title}
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={`/docs/${next.id}`}
              className="text-right text-dl-primary hover:underline"
            >
              {ui.next}: {next.text[locale].title} →
            </Link>
          )}
        </div>
      </article>
      {headings.length > 1 && (
        <aside className="sticky top-24 hidden w-56 shrink-0 self-start xl:block">
          <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-dl-faint">
            {ui.contents}
          </div>
          <ul className="flex flex-col gap-1 border-l border-dl-border">
            {headings.map(heading => (
              <li key={heading}>
                <Link
                  to={`/docs/${page.id}#${slugify(heading)}`}
                  className="-ml-px block border-l border-transparent py-0.5 pl-3 text-base text-dl-muted hover:border-dl-strong hover:text-dl-text"
                >
                  {heading}
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  );
}

/** Links between pages stay in the app (`/docs/…`); the others open in a new tab. */
function DocLink({
  href = '',
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (href.startsWith('/') || href.startsWith('#')) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  );
}

/**
 * A screenshot: framed, its alt text as a caption, opening full size in a new tab. Spans, not a figure: Markdown
 * puts an image inside a paragraph.
 */
function Screenshot({
  src,
  alt,
  title,
}: React.ImgHTMLAttributes<HTMLImageElement>) {
  return (
    <span className="my-6 block">
      <a href={src} target="_blank" rel="noreferrer" className="block">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="m-0 w-full rounded-xl border border-dl-border shadow-sm"
        />
      </a>
      {(title || alt) && (
        <span className="mt-2 block text-center text-base text-dl-faint">
          {title || alt}
        </span>
      )}
    </span>
  );
}

export default DocsPage;
